import React from "react";

import {
  FiCompass,
  FiImage,
  FiMapPin,
  FiRefreshCw,
  FiRotateCw,
  FiSearch,
  FiSliders,
} from "react-icons/fi";

import Reveal from "../../../components/ui/Reveal";

import {
  COMMERCES_DIR_OPTIONS,
  COMMERCES_SORT_OPTIONS,
} from "../data/commercesUiData";

import { cn } from "../utils/commercesUtils";

export default function CommerceFilters({
  loading,
  filters,
  geo,
  categories = [],
  villes = [],
  total = 0,
  allTotal = 0,
  onChange,
  onCategoryClick,
  onGeoToggle,
  onReset,
  onRefresh,
}) {
  return (
    <Reveal>
      <section className="min-w-0 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
        {/* ------------------------------------------------------------------ */}
        {/* Recherche + actions                                                */}
        {/* ------------------------------------------------------------------ */}

        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          {/* Recherche */}

          <div className="min-w-0 space-y-2">
            <label
              htmlFor="commerce-search"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
            >
              <FiSearch className="h-4 w-4 shrink-0" />

              Recherche instantanée
            </label>

            <div className="relative min-w-0">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                id="commerce-search"
                type="text"
                value={filters.q}
                onChange={(event) =>
                  onChange(
                    "q",
                    event.target.value,
                  )
                }
                placeholder="Nom, activité, produit, adresse…"
                className="
                  h-12
                  min-w-0
                  w-full
                  max-w-full
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  pl-11
                  pr-4
                  text-sm
                  font-semibold
                  text-slate-800
                  outline-none
                  transition

                  placeholder:text-slate-400

                  focus:border-[#1277B9]
                  focus:ring-4
                  focus:ring-[#1277B9]/10

                  dark:border-slate-700
                  dark:bg-slate-950
                  dark:text-white
                  dark:placeholder:text-slate-500
                "
              />
            </div>
          </div>

          {/* Actions */}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
            <button
              type="button"
              onClick={onReset}
              className="
                inline-flex
                min-h-12
                min-w-0
                items-center
                justify-center
                gap-2
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                transition

                hover:bg-slate-50

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
                dark:hover:bg-slate-900
              "
            >
              <FiRefreshCw className="h-4 w-4 shrink-0" />

              Réinitialiser
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Actualiser la liste depuis le serveur"
              className="
                inline-flex
                min-h-12
                min-w-0
                items-center
                justify-center
                gap-2
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                transition

                hover:bg-slate-50

                disabled:cursor-not-allowed
                disabled:opacity-60

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
                dark:hover:bg-slate-900
              "
            >
              <FiRotateCw
                className={cn(
                  "h-4 w-4 shrink-0",
                  loading &&
                    "animate-spin",
                )}
              />

              Actualiser
            </button>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Catégories                                                         */}
        {/* ------------------------------------------------------------------ */}

        <div className="mt-5 min-w-0 space-y-3">
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <FiSliders className="h-4 w-4 shrink-0" />

              Catégories
            </p>

            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              {loading
                ? "Chargement…"
                : `${total} affiché${total > 1 ? "s" : ""} sur ${allTotal}`}
            </p>
          </div>

          {/* -------------------------------------------------------------- */}
          {/* MOBILE : SELECT                                                 */}
          {/* -------------------------------------------------------------- */}

          <div className="min-w-0 md:hidden">
            <select
              value={
                filters.categorie ||
                ""
              }
              onChange={(event) =>
                onCategoryClick(
                  event.target.value,
                )
              }
              className="
                h-12
                min-w-0
                w-full
                max-w-full
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                outline-none
                transition

                focus:border-[#1277B9]
                focus:ring-4
                focus:ring-[#1277B9]/10

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
              "
            >
              <option value="">
                Toutes les catégories
              </option>

              {categories.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* -------------------------------------------------------------- */}
          {/* DESKTOP : BADGES                                                */}
          {/* -------------------------------------------------------------- */}

          <div className="hidden min-w-0 flex-wrap gap-2 md:flex">
            <button
              type="button"
              onClick={() =>
                onCategoryClick("")
              }
              className={cn(
                "inline-flex items-center rounded-full border px-4 py-2 text-sm font-bold transition",

                !filters.categorie
                  ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900",
              )}
            >
              Tous
            </button>

            {categories.map(
              (category) => {
                const active =
                  category ===
                  filters.categorie;

                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() =>
                      onCategoryClick(
                        category,
                      )
                    }
                    className={cn(
                      "inline-flex max-w-full items-center rounded-full border px-4 py-2 text-sm font-bold transition",

                      active
                        ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm"
                        : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900",
                    )}
                  >
                    <span className="max-w-[220px] truncate">
                      {category}
                    </span>
                  </button>
                );
              },
            )}
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Autres filtres                                                     */}
        {/* ------------------------------------------------------------------ */}

        <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* Ville */}

          <div className="min-w-0 space-y-2">
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <FiMapPin className="h-4 w-4 shrink-0" />

              Ville
            </label>

            <select
              value={filters.ville}
              onChange={(event) =>
                onChange(
                  "ville",
                  event.target.value,
                )
              }
              className="
                h-12
                min-w-0
                w-full
                max-w-full
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                outline-none
                transition

                focus:border-[#1277B9]
                focus:ring-4
                focus:ring-[#1277B9]/10

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
              "
            >
              <option value="">
                Toutes
              </option>

              {villes.map(
                (ville) => (
                  <option
                    key={ville}
                    value={ville}
                  >
                    {ville}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Tri */}

          <div className="min-w-0 space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Trier par
            </label>

            <select
              value={filters.sort}
              onChange={(event) =>
                onChange(
                  "sort",
                  event.target.value,
                )
              }
              className="
                h-12
                min-w-0
                w-full
                max-w-full
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                outline-none
                transition

                focus:border-[#1277B9]
                focus:ring-4
                focus:ring-[#1277B9]/10

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
              "
            >
              {COMMERCES_SORT_OPTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={
                      option.value ===
                        "distance" &&
                      !filters.useGeo
                    }
                  >
                    {option.label}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Ordre */}

          <div className="min-w-0 space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Ordre
            </label>

            <select
              value={filters.dir}
              onChange={(event) =>
                onChange(
                  "dir",
                  event.target.value,
                )
              }
              className="
                h-12
                min-w-0
                w-full
                max-w-full
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                outline-none
                transition

                focus:border-[#1277B9]
                focus:ring-4
                focus:ring-[#1277B9]/10

                dark:border-slate-700
                dark:bg-slate-950
                dark:text-slate-100
              "
            >
              {COMMERCES_DIR_OPTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Geo / Photo */}

          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
            <button
              type="button"
              onClick={onGeoToggle}
              className={cn(
                "inline-flex min-h-12 min-w-0 items-center justify-center gap-2 rounded-2xl border px-3 text-sm font-bold transition",

                filters.useGeo
                  ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                  : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900",
              )}
            >
              <FiCompass className="h-4 w-4 shrink-0" />

              <span className="truncate">
                {geo?.loading
                  ? "Localisation…"
                  : "Autour de moi"}
              </span>
            </button>

            <button
              type="button"
              onClick={() =>
                onChange(
                  "hasPhoto",
                  !filters.hasPhoto,
                )
              }
              className={cn(
                "inline-flex min-h-12 min-w-0 items-center justify-center gap-2 rounded-2xl border px-3 text-sm font-bold transition",

                filters.hasPhoto
                  ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                  : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900",
              )}
            >
              <FiImage className="h-4 w-4 shrink-0" />

              Avec photo
            </button>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Erreur géolocalisation                                             */}
        {/* ------------------------------------------------------------------ */}

        {geo?.error ? (
          <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
            {geo.error}
          </p>
        ) : null}
      </section>
    </Reveal>
  );
}